import { useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { DataTable } from "~components/DataTable/DataTable.tsx";
import { DEFAULT_PAGE_SIZE_OPTIONS } from "~constants/pagination.ts";
import { PermissionNames } from "~enums/permissions.ts";
import { useHandlePagination } from "~hooks/useHandlePagination.ts";
import { useSportConfiguration } from "~hooks/useSportConfiguration.ts";
import { ConferenceTableColumns } from "~pages/Conferences/ConferenceListPage/enums/conferenceTableColumns.ts";
import { useFetchConferenceList } from "~pages/Conferences/ConferenceListPage/hooks/useFetchConferenceList.ts";
import { useGetConferenceTableColumns } from "~pages/Conferences/ConferenceListPage/hooks/useGetConferenceTableColumns.tsx";
import { useConferenceService } from "~pages/Conferences/hooks/useConferenceService.ts";
import type { Conference } from "~types/conference.ts";

const DEFAULT_SORTER = { columnKey: ConferenceTableColumns.NameLocal, order: "ascend" as const };

const ConferenceListPage = () => {
  const paginationConfig = useHandlePagination(10, 1, DEFAULT_PAGE_SIZE_OPTIONS);
  const { sport, customerId, organizationId } = useParams();
  const { formatMessage } = useIntl();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState(location.state?.search || "");
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [massEditMode, setMassEditMode] = useState(false);

  const { uiConfiguration } = useSportConfiguration();
  const dataTableUiConfiguration = uiConfiguration.pages.conferences.listPage;
  const columns = useGetConferenceTableColumns(massEditMode);

  const { resetPagination } = paginationConfig;

  const handleSearch = useCallback(
    (value: string) => {
      resetPagination();
      setSearchText(value);
    },
    [resetPagination],
  );

  const listParams = useMemo(
    () => ({
      limit: paginationConfig.pageSize.toString(),
      offset: paginationConfig.offset.toString(),
      sortBy: sortBy || "",
      nameLocalContains: searchText,
    }),
    [paginationConfig.pageSize, paginationConfig.offset, searchText, sortBy],
  );

  const { data, refetch, isPending } = useFetchConferenceList({
    customParams: listParams,
    enabled: sortBy !== null,
  });
  const { deleteConference, isDeletePending } = useConferenceService();

  const getNavigateUrl = (conferenceId = "") => `/${customerId}/admin/${sport}/${organizationId}/conferences/manage/${conferenceId}`;

  return (
    <DataTable<Conference>
      handleRefresh={async () => {
        await refetch();
      }}
      title={formatMessage({ id: "conferences" })}
      columns={columns}
      rowActions={{
        base: {
          manage: {
            onClick: (row) => navigate(getNavigateUrl(row.conferenceId)),
          },
          delete: {
            onClick: (row) => deleteConference(row.conferenceId),
            loading: isDeletePending,
          },
        },
      }}
      rowNavigation={{
        getNavigateUrl: (row) => getNavigateUrl(row.conferenceId),
      }}
      rowKey={"conferenceId"}
      rowNameKey={"nameLocal"}
      dataSource={data?.data || []}
      columnChooserLocalStorageKey={`${sport}.tables.settings.columns.conferences`}
      isDataLoading={isPending}
      handleAdd={() => navigate(getNavigateUrl())}
      addButtonIntlKey={"conference"}
      searchConfig={{
        handleSearch,
        searchText,
      }}
      columnSortConfig={{
        sorterLocalStorageKey: `${sport}.tables.settings.sort.conferences`,
        defaultSorter: DEFAULT_SORTER,
        onSortChange: setSortBy,
      }}
      permissionNames={[PermissionNames.CONFERENCE]}
      onMassEditModeChangedHandler={setMassEditMode}
      massEditMode={massEditMode}
      dataTableUiConfiguration={dataTableUiConfiguration}
      paginationConfig={{ ...paginationConfig, hasNext: data?.hasNextLink }}
    />
  );
};

export default ConferenceListPage;
